import { useRef, useEffect } from 'react';
import { PapiGallection, PapiDeviation, PapiPremiumFolderData } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { OverlayContextProvider } from '@wix/da-react-context/pkg/OverlayContext';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import FolderDescription from '../FolderDescription';
import { GallectionType } from '../../../../../../types/gallection';
import Pagination from '@wix/da-shared-react/pkg/Pagination';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import GallectionDeviationsTorpedo from '../../GallectionDeviationsTorpedo';
import FolderPaywall from '../../FolderPaywall';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import FolderBreadcrumbs from '../FolderBreadcrumbs/';
import generatePaginationUrl from '../../../../../utils/generatePaginationUrl';
import { BiData, GalleryPurchaseBiEvent } from '@wix/da-bi/pkg/events';

import s from './FolderPagedContent.scss';

export interface Props {
  streamId: string;
  folderId: number;
  subfolderId?: number;
  currentPage: number;
  totalPages: number;
  requestUrl: string;
  folder: PapiGallection;
  subfolder?: PapiGallection;
  gallectionType: GallectionType;
  deviations: PapiDeviation[];
  isInitialized: boolean;
  hasMore: boolean;
  isSelected: boolean;
  isFetching: boolean;
  initFetch: () => void;
  isOwner: boolean;
  className?: string;
  isMobile?: boolean;
  scrollIntoViewWhenLoaded?: boolean;
  justExitedDuperbrowse?: boolean;
}

function initFetchIfNeeded(props: Props) {
  const { deviations, isInitialized, hasMore, isFetching, initFetch } = props;
  if (!deviations.length && (hasMore || !isInitialized) && !isFetching) {
    initFetch();
  }
}

function FolderPagedContent(props: Props) {
  const folderRef = useRef<HTMLElement | null>(null);
  const torpedoRef = useRef<HTMLElement | null>(null);
  const {
    subfolder,
    currentPage,
    totalPages,
    hasMore,
    requestUrl,
    deviations,
    isFetching,
    folder,
    className,
    gallectionType,
    streamId,
    isOwner,
    initFetch,
    isInitialized,
  } = props;

  useEffect(() => {
    initFetchIfNeeded(props);
  }, [deviations, isInitialized, hasMore, isFetching, initFetch, props]);

  const { pointPrice, isPremiumLocked } = normalizePremiumFolderData(folder.premiumFolderData);
  const isCollection = gallectionType === GallectionType.COLLECTION;
  const nextPage = currentPage + 1;
  const fakeTotalPages = hasMore ? nextPage : currentPage;
  const showPageNumbers = !!totalPages;
  const showPaywall = isPremiumLocked && !isOwner;

  return (
    <OverlayContextProvider
      value={{
        showAuthorTooltip: isCollection,
        showThumbStatusIndicators: false,
      }}
    >
      <div className={className} ref={folderRef as any}>
        <ErrorBoundary debugComponent="FolderPagedContent">
          <FolderDescription folder={folder} className={s['folder-description']} />
          {subfolder && <FolderBreadcrumbs />}

          {!showPaywall ? (
            deviations.length > 0 && (
              <div ref={torpedoRef as any}>
                <BiLoggerContextProvider
                  value={{
                    section_typeid: gallectionTypeToResourceType(gallectionType),
                    sectionid: folder.folderId,
                    authorid: folder.owner.useridUuid,
                  }}
                >
                  <GallectionDeviationsTorpedo
                    streamId={streamId}
                    gallectionType={gallectionType}
                    deviations={deviations}
                  />
                </BiLoggerContextProvider>
              </div>
            )
          ) : (
            <FolderPaywall
              premiumFolderData={{
                ...(folder.premiumFolderData as PapiPremiumFolderData),
                galleryUrl: Url.userGalleryLink({
                  username: folder.owner.username,
                  gallectionType: 'gallery',
                  folderId: folder.folderId,
                  folderName: folder.name,
                }),
              }}
              folder={folder}
              biData={BiData<GalleryPurchaseBiEvent>({
                evid: 361,
                points: pointPrice,
                typeid: gallectionTypeToResourceType(gallectionType) ?? 0,
                itemid: folder.folderId,
                galleryid: folder.folderId,
                authorid: folder.owner.useridUuid,
              })}
            />
          )}

          {!isPremiumLocked && totalPages > 1 && (
            <div className={s['button-wrapper']}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages ? totalPages : fakeTotalPages}
                showPageNumbers={showPageNumbers}
                getPageUrl={page => generatePaginationUrl(page, requestUrl)}
              />
            </div>
          )}
          {isFetching && <LoadingIndicator />}
        </ErrorBoundary>
      </div>
    </OverlayContextProvider>
  );
}

FolderPagedContent.displayName = 'FolderPagedContent';

export default FolderPagedContent;
